import React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import Settings from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Logout from "@mui/icons-material/Logout";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { logout } from "../features/slices/Auth";
import { useLoader } from "../context/LoaderContext";
import { useNotification } from "../context/NotificationContext";

export function AccountMenu(): JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { showLoader, hideLoader } = useLoader();
  const { notify } = useNotification();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    navigate("/account");
    handleClosePopover();
  };

  const handleAdmin = () => {
    navigate(`management`);
    handleClosePopover();
  };

  const handleLogout = async () => {
    try {
      showLoader();
      const resultAction = await dispatch(logout());
      if (resultAction.payload) {
        localStorage.removeItem("redirectTo");
        hideLoader();
        notify("Logout successful!", "success");
        navigate("/signin");
      } else {
        notify("Logout failed.", "error");
        hideLoader();
      }
    } catch (error) {
      notify("Logout failed.", "error");
      hideLoader();
      console.error("Failed to logout:", error);
    }
    handleClosePopover();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleAvatarClick}
          size="small"
          sx={{ height: { xs: 36, sm: 40 }, width: { xs: 36, sm: 40 } }}
        >
          <Avatar
            sx={{
              width: { xs: 30, sm: 32 },
              height: { xs: 30, sm: 32 },
              backgroundColor: "primary.main",
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            {user?.username ? user.username.charAt(0).toUpperCase() : "A"}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{ sx: { width: "240px" } }}
      >
        <Box sx={{ p: "16px 20px" }}>
          <Typography variant="subtitle1">
            {user?.username || "User"}
          </Typography>
          {user?.email && (
            <Typography color="text.secondary" variant="body2">
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuList
          disablePadding
          sx={{ p: "8px", "& .MuiMenuItem-root": { borderRadius: 1 } }}
        >
          <MenuItem onClick={handleAdmin}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            Manage
          </MenuItem>
          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
}

export default AccountMenu;
